import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/lib/Modal';
import Button from './Button';
import ModalHeader from './ModalHeader';
import Wizard from './Wizard';
import AlertMessage from './AlertMessage';
import i18n from '../../src/i18n/translate';
import { AlertMessageTypes } from '../constants/index';
import { formatClassName } from '../Util';
import Icon from './Icon';

export default class WizardModal extends React.Component {
  static propTypes = {
    allowCancel: PropTypes.bool,
    branded: PropTypes.bool,
    children: PropTypes.any,
    description: PropTypes.string,
    descriptionComponentClass: PropTypes.elementType,
    disableContinueButton: PropTypes.bool,
    errorMessage: PropTypes.string,
    finalButtonLabel: PropTypes.string,
    footerButtonAttributes: PropTypes.shape({
      cancelButton: PropTypes.object,
      nextButton: PropTypes.object,
      previousButton: PropTypes.object
    }),
    footerText: PropTypes.string,
    hasScrollableForm: PropTypes.bool,
    hasStepIndicator: PropTypes.bool,
    initialStep: PropTypes.number,
    limitHeight: PropTypes.bool,
    modalSize: PropTypes.string,
    onCancel: PropTypes.func,
    onResetSteps: PropTypes.func,
    renderExtraButtons: PropTypes.func,
    title: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.func
    ]),
    titleComponentClass: PropTypes.elementType,
    useStepTitle: PropTypes.bool,
    wizardClass: PropTypes.string,
    customFooter: PropTypes.instanceOf(Modal.Footer)
  };

  static defaultProps = {
    allowCancel: true,
    disableContinueButton: false,
    hasStepIndicator: true
  }

  constructor(props) {
    super(props);

    this.state = {
      wizardStep: this.props.initialStep || 0,
      finalButtonLabel: this.props.finalButtonLabel || i18n.t('Done')
    };

    this.modalBody = React.createRef();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.finalButtonLabel !== this.props.finalButtonLabel) {
      this.setState({ finalButtonLabel: this.props.finalButtonLabel })
    }
  }

  buildWizard = () => {
    return (
      <Wizard
        wizardStep={this.state.wizardStep}
        initialStep={this.props.initialStep}
        onCancel={this.props.onCancel}
        hasButtons={false}
        hasStepIndicator={false}
        overrideSteps={true}
      >
        {React.Children.map(this.props.children, (child) => {
          return React.cloneElement(child, {
            goingToStep: this.state.goingToStep,
            goingDirection: this.state.goingDirection,
            stepChangeComplete: this.stepChangeComplete,
            stepChangeCancel: this.stepChangeCancel,
            canceling: this.state.canceling,
            cancelComplete: this.props.onCancel,
            cancelClose: this.onCancelClose,
            resetSteps: this.resetSteps
          });
        })}
      </Wizard>
    );
  }

  buildStepIndicator = () => {
    var numChildren = React.Children.count(this.props.children);
    return (
      React.Children.map(this.props.children, (child, i) => {
        if (child.props.title) {
          return (
            <div className="stepindicator-child">
              <div className={(i <= this.state.wizardStep) ? "circle complete" : "circle"}>
                <div>{i + 1}</div>
              </div>
              <div className="stepindicatorchild-label">{child.props.title}</div>
              {numChildren - 1 > i ? <div className="separator-bar"><div /></div> : ""}
            </div>
          );
        }
      })
    );
  }

  nextStep = () => {
    this.setState({ goingToStep: this.state.wizardStep + 1, goingDirection: 'forward' }, () => {
      // ECA-3406 is for focus the first element with has-error class
      const inputWithError = document.querySelector('input:invalid');
      if (inputWithError) {
        inputWithError.focus();
      }
    });
  }

  stepChangeCancel = () => {
    this.setState({ goingToStep: null, goingDirection: null });
  }

  stepChangeComplete = () => {
    if (this.state.goingToStep === React.Children.count(this.props.children)) {
      this.setState({ goingToStep: null, goingDirection: null }, () => this.onCancelClose(true));
    } else {
      this.setState({ goingToStep: null, goingDirection: null, wizardStep: this.state.goingToStep });
    }
  }

  previousStep = () => {
    if (this.state.wizardStep > 0) {
      this.setState({ goingToStep: this.state.wizardStep - 1, goingDirection: 'backward' });
    }
  }

  resetSteps = () => {
    this.setState({ wizardStep: 0, goingToStep: 0, goingDirection: 'backward' });

    if (this.props.onResetSteps) {
      this.props.onResetSteps();
    }

    const modalBody = ReactDOM.findDOMNode(this.modalBody);
    if (modalBody) {
      modalBody.scrollTo(0, 0);
    }
  }

  onCancel = () => {
    this.setState({ canceling: true, lastFocus: document.activeElement });
  }

  onCancelClose = (close) => {
    this.setState({ canceling: false }, () => {
      if (close) {
        if (this.props.onCancel) {
          this.props.onCancel();
        }
      } else {
        if (this.state.lastFocus && this.state.lastFocus.focus) {
          setTimeout(() => this.state.lastFocus.focus(), 250);
        }
      }
    });
  }

  get title() {
    const { children, title, useStepTitle } = this.props;
    const { wizardStep } = this.state;
    if (useStepTitle) {
      const currentStep = React.Children.toArray(children)[wizardStep];
      if (currentStep) {
        return currentStep.props.title;
      }
    }
    if (typeof title === 'function') {
      return title(wizardStep);
    }
    return title;
  }

  renderScrollableFooter = () => {
    const { branded } = this.props;
    return (
      <Modal.Footer className="scrollable sticky-footer">
        <div className="info-text m-b-15">
          <Icon iconType="swap-vertical" additionalClasses="m-r-10" />
          {i18n.t('Scroll to see all required fields')}
        </div>
        <Button
          className="btn-full-size m-0 p-15"
          branded={branded}
          style="primary"
          onClick={this.nextStep}
          disabled={this.props.disableContinueButton || !!this.state.goingToStep}
          data-action-button="complete_wizard"
        >
          {this.state.finalButtonLabel}
        </Button>
        {this.props.footerText && this.props.footerText}
      </Modal.Footer>
    );
  }

  renderExtraFooterButtons = (isLastStep) => {
    const { renderExtraButtons } = this.props;
    if (!renderExtraButtons || typeof renderExtraButtons !== 'function') {
      return null;
    }

    return renderExtraButtons(
      this.state.wizardStep,
      this.state.goingToStep,
      isLastStep,
      this.previousStep,
      this.nextStep,
      this.onCancel
    );
  }

  renderDefaultFooter = () => {
    const { footerButtonAttributes, footerText, renderExtraButtons, branded } = this.props;
    const { wizardStep, goingToStep } = this.state;
    const isLastStep = wizardStep === React.Children.count(this.props.children) - 1;

    return (
      <Modal.Footer className="sticky-footer" >
        {wizardStep > 0 ? (
          <Button
            {...footerButtonAttributes?.previousButton}
            className="secondary-light pull-left"
            icon="chevron-left"
            onClick={this.previousStep}
            style="secondary"
          >
            {i18n.t(" Previous")}
          </Button>
        ) : null}
        {this.props.allowCancel ? (
          <Button
            {...footerButtonAttributes?.cancelButton}
            className="secondary-light"
            disabled={!!goingToStep}
            onClick={this.onCancel}
            style="secondary"
          >
            {i18n.t('Cancel')}
          </Button>
        ) : null}
        <Button
          {...footerButtonAttributes?.nextButton}
          branded={branded}
          disabled={!!goingToStep}
          icon={goingToStep ? 'spinner' : null}
          onClick={this.nextStep}
          rightIcon={!renderExtraButtons ? 'chevron-right' : null}
          style="primary"
        >
          {isLastStep ? this.state.finalButtonLabel : i18n.t("Next")}
        </Button>
        {this.renderExtraFooterButtons(isLastStep)}
        {footerText && footerText}
      </Modal.Footer>
    )
  }

  render() {
    const { allowCancel, children, description, errorMessage, hasScrollableForm,
      hasStepIndicator, limitHeight, modalSize, wizardClass, titleComponentClass,
      descriptionComponentClass } = this.props;
    const wizardClasses = formatClassName([
      'wizard-modal',
      modalSize && `modal-size-${modalSize}`,
      wizardClass,
      { 'scrolling-modal': limitHeight }
    ]);

    const headerClasses = formatClassName([
      { 'scrollable': hasScrollableForm },
      { 'has-step-indicator': hasStepIndicator }
    ])

    return (
      <div>
        <Modal id="form-modal" bsSize={modalSize} className={wizardClasses}
          show={true} onHide={this.onCancel} backdrop={allowCancel ? true : 'static'} aria-labelledby="form-modal-header">
          <ModalHeader
            className={headerClasses}
            onCancel={this.onCancel}
            canClickOff={allowCancel && !hasScrollableForm}
            title={this.title}
            titleId="form-modal-header"
            titleComponentClass={titleComponentClass}
            description={description}
            descriptionComponentClass={descriptionComponentClass}
          />
          {hasStepIndicator && (React.Children.count(children)) > 1 &&
            <div className="step-indicator">
              {this.buildStepIndicator()}
            </div>}
          {errorMessage && (
            <AlertMessage message={errorMessage} messageType={AlertMessageTypes.danger} visible={true} />
          )}
          <Modal.Body ref={ref => { this.modalBody = ref }}>
            {this.buildWizard()}
          </Modal.Body>

          {this.props.customFooter ? this.props.customFooter : (hasScrollableForm ? this.renderScrollableFooter() : this.renderDefaultFooter())}
        </Modal>
      </div>);
  }
}
